(() => {
  // ---------------------------------------------------------------------------
  // HR quiz round trip (owner brief 2026-07-30)
  //
  // The cart's "Save an extra 10%" link used to send a shopper to the quiz and
  // leave them there, which is the worst possible moment to lose someone. Now:
  //   1. tapping that link records that the quiz was entered FROM the cart,
  //   2. the quiz sends them back with ?hrcode=CODE (and a sessionStorage copy),
  //   3. this script pre-fills the discount field, applies the code, and says so.
  // If the automatic apply does not take, the code stays in the field and the
  // note tells them to tap Apply. It is never lost either way.
  //
  // The guards are on `window` on purpose: this file is loaded twice on the cart
  // page (once by main-cart-items, once by the drawer), so a module-scoped flag
  // would let the returned code be applied twice.
  // ---------------------------------------------------------------------------
  const ORIGIN_KEY = 'hrCartQuizOrigin';
  const RETURN_URL_KEY = 'hrCartQuizReturnUrl';
  const PENDING_CODE_KEY = 'hrPendingDiscountCode';
  // A discount code is a short opaque token. Anything that is not one is thrown
  // away rather than written into the DOM or sent anywhere.
  const CODE_SHAPE = /^[A-Z0-9][A-Z0-9._-]{2,63}$/;

  function ssGet(key) {
    try { return sessionStorage.getItem(key) || ''; } catch (e) { return ''; }
  }
  function ssSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
  }
  function ssRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }

  // The cart drawer's "No code? Take the quiz for an extra 10%" promo card is
  // removed (owner brief 2026-08-04: the quiz is retired, replaced by the
  // wheel, and 15% is now automatic via HARVEST15). Its click tracker
  // (data-hr-quiz-from-cart) is removed with it -- the element it targeted no
  // longer exists in snippets/cart-discount-codes-custom.liquid. The rest of
  // this file (return-code note, discount apply/remove) is untouched.
  function readReturnedCode() {
    let code = '';
    try {
      code = (new URLSearchParams(window.location.search).get('hrcode') || '').trim();
    } catch (e) {}
    if (!code) code = ssGet(PENDING_CODE_KEY).trim();
    code = code.toUpperCase();
    return CODE_SHAPE.test(code) ? code : '';
  }

  function wantsCartOpen() {
    try { return new URLSearchParams(window.location.search).get('hrcart') === '1'; } catch (e) { return false; }
  }

  // Take the code out of the address bar once it is safely in sessionStorage, so
  // a refresh or a shared link does not replay the apply.
  function cleanReturnParamsFromUrl() {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('hrcode') && !url.searchParams.has('hrcart')) return;
      url.searchParams.delete('hrcode');
      url.searchParams.delete('hrcart');
      const qs = url.searchParams.toString();
      window.history.replaceState({}, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
    } catch (e) {}
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    if (drawer.classList.contains('active')) return true;
    try { drawer.open(); return true; } catch (e) { return false; }
  }

  // The cart drawer replaces its own innerHTML on every cart update, so the note
  // has to be re-rendered rather than written once. State lives on window for
  // the same double-load reason as the guards above.
  function renderReturnedNote() {
    const note = window.hrReturnedCodeNote;
    const nodes = document.querySelectorAll('[data-hr-code-returned]');
    if (!nodes.length) return;
    nodes.forEach((el) => {
      const textEl = el.querySelector('[data-hr-code-returned-text]');
      if (!note || !textEl) {
        el.hidden = true;
        return;
      }
      while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
      if (note.applied) {
        const strong = document.createElement('strong');
        strong.textContent = note.code;
        textEl.appendChild(document.createTextNode('Code '));
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode(' is applied to your order.'));
      } else {
        const strong = document.createElement('strong');
        strong.textContent = note.code;
        textEl.appendChild(document.createTextNode('Your code '));
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode(' is in the field. Tap Apply to use it.'));
      }
      if (el.hidden) {
        el.hidden = false;
        el.classList.add('is-hr-entering');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => el.classList.remove('is-hr-entering'));
        });
      }
    });
  }

  function initDiscountScript() {
    const appliedDiscountsContainer = document.getElementById('applied-discounts');
    const applyBtn = document.getElementById('apply-discount-btn');
    const discountInput = document.getElementById('discount-input');

    if (!appliedDiscountsContainer || !applyBtn || !discountInput) return;

    const strings = window.cartDiscountStrings || {};
    const MSG_NO_CODE = strings.errorNoCode || "Please enter a discount code.";
    const MSG_ALREADY_APPLIED = strings.errorAlreadyApplied || "That discount code is already applied.";
    const MSG_INVALID = strings.errorInvalid || "Discount code is invalid or not applicable.";
    // NETWORK-FAILURE GUARD (live bug, 2026-08-27): applyDiscounts() used to let
    // a dropped /cart.js fetch throw straight out of both the Apply and Remove
    // paths -- an unhandled promise rejection, and on some engines the busy
    // guard never reached its reset. Both paths now catch that case and show
    // this instead of silently doing nothing (the same "nothing happened"
    // silence that made the owner's shopper tap Remove five times).
    const MSG_NETWORK = strings.errorNetwork || "Could not reach the cart. Check your connection and try again.";

    function clearMessages() {
      const errDiv = document.getElementById('discount-error-message');
      if (errDiv) {
        errDiv.style.display = 'none';
        errDiv.textContent = '';
      }
    }

    function showError(msg) {
      const errDiv = document.getElementById('discount-error-message');
      if (errDiv) {
        errDiv.style.display = 'block';
        errDiv.textContent = msg;
      }
    }

    function getCurrentDiscounts() {
      const pills = document.querySelectorAll('#applied-discounts .discount-pill');
      return Array.from(pills).map((pill) => pill.getAttribute('data-code').toUpperCase());
    }

    function triggerCartUpdate() {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.updateQuantity === 'function') {
        const qtyInput =
          document.getElementById('Drawer-quantity-1') ||
          document.getElementById('Quantity-1');
        if (qtyInput) {
          const currentQty = parseInt(qtyInput.value, 10) || 1;
          cartDrawerItems.updateQuantity(1, currentQty);
          return;
        } else {
          cartDrawerItems.onCartUpdate();
          return;
        }
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        const qtyInput =
          document.getElementById('Quantity-1') ||
          document.getElementById('Drawer-quantity-1');
        if (qtyInput) {
          const currentQty = parseInt(qtyInput.value, 10) || 1;
          cartItems.updateQuantity(1, currentQty);
          return;
        } else {
          cartItems.onCartUpdate();
          return;
        }
      }
    }

    async function applyDiscounts(discountCodes = []) {
      let isRemoving = false;
      if (discountCodes.length === 0) {
        discountCodes = ['REMOVE_SHOPIFY_CODE'];
        isRemoving = true;
      }
      const newlyAddedCode = discountCodes[discountCodes.length - 1];

      // ROOT CAUSE FIX (owner brief 2026-08-04): this used to always hit
      // `/checkout?discount=CODE`. That endpoint only ever primes the
      // CHECKOUT page's discount field for when the shopper eventually lands
      // there -- it never touches the CART's own session, so cart.js /
      // cart_level_discount_applications (and therefore the drawer's
      // subtotal) never changed. That is exactly the bug the owner hit: the
      // hooray popup fired, but nothing was ever actually applied to the
      // cart, so it never affected the total. Removing a code has no direct
      // Shopify endpoint, so feeding checkout a bogus code (which retires
      // whatever real code the session was carrying) is kept for that path.
      // Applying a real code now uses Shopify's own "discount URL" route,
      // `/discount/CODE?redirect=...`, which sets the discount on the
      // session AND is reflected immediately in the next /cart.js read.
      //
      // FIX (2026-08-05, live QA): removal used to hit
      // `/checkout?discount=REMOVE_SHOPIFY_CODE`, which -- same as the old
      // apply bug above -- only primes the CHECKOUT page's discount field and
      // never touches the CART session. That left a code (HARVEST10, or a
      // shopper's own affiliate code like JADE20) permanently stuck in the
      // cart's discount session: a `/cart/clear.js` + fresh add-to-cart did
      // NOT clear it, so it silently re-applied and even stacked on a
      // subscription line, which must never take a code. `/cart/update.js`
      // with an empty `discount` is the only endpoint confirmed (live) to
      // actually zero out `cart.discount_codes`.
      try {
        if (isRemoving) {
          await fetch(`/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: '' })
          });
        } else {
          await fetch(`/discount/${encodeURIComponent(newlyAddedCode)}?redirect=%2Fcart`, { method: 'GET' });
        }
      } catch (err) {}

      let cartData;
      try {
        const cartRes = await fetch('/cart.js');
        cartData = await cartRes.json();
      } catch (err) {
        // Dropped connection or bad response: report it as "unknown", never
        // let it throw out of here uncaught. Callers show MSG_NETWORK.
        return { success: false, invalidCodes: [], networkError: true };
      }

      const validSet = new Set();
      (cartData.cart_level_discount_applications || []).forEach(d => validSet.add(d.title.toUpperCase()));
      cartData.items.forEach(item => {
        (item.line_level_discount_allocations || []).forEach(a => {
          validSet.add(a.discount_application.title.toUpperCase());
        });
      });

      const invalidCodes = discountCodes.filter(c => !validSet.has(c.toUpperCase()));
      const codeIsInvalid = invalidCodes.includes(newlyAddedCode);

      if (newlyAddedCode === 'REMOVE_SHOPIFY_CODE' || !codeIsInvalid) {
        triggerCartUpdate();
      }
      return {
        success: !codeIsInvalid || isRemoving,
        invalidCodes
      };
    }

    // Exposed for the cart "hooray" 15%-off incentive (assets/hr-cart-hooray.js),
    // which auto-applies HARVEST15 on the first add-to-cart of a session and needs
    // the same checkout-discount + cart-refresh mechanism used everywhere else,
    // never a second/parallel discount endpoint. Re-assigned on every re-init
    // (cartUpdate re-runs this function) but that is harmless, same closure shape.
    window.hrApplyDiscounts = applyDiscounts;
    window.hrGetCurrentDiscounts = getCurrentDiscounts;

    // Shared by the Apply button and by the automatic apply of a code carried
    // back from the quiz, so both paths behave and fail identically.
    async function submitEnteredCode() {
      clearMessages();
      const existingCodes = getCurrentDiscounts();
      /* Resolve the field LIVE (regression fix, 2026-08-27). cart-drawer.js
         swaps the children of #discount-code-container on every cart render,
         so the node captured when initDiscountScript() ran is detached by the
         time a shopper types and taps Apply. Reading .value off that detached
         node returned "" -- so this bailed out as "no code entered" and never
         issued a /cart/update request. Measured on an iPhone 13 profile:
         typing HARVEST10 and tapping Apply produced zero network traffic. */
      const liveInput = document.getElementById('discount-input');
      const newCode = ((liveInput || discountInput).value || '').trim().toUpperCase();
      if (!newCode) {
        showError(MSG_NO_CODE);
        return { ok: false, reason: 'empty' };
      }
      if (existingCodes.includes(newCode)) {
        showError(MSG_ALREADY_APPLIED);
        return { ok: true, reason: 'already-applied' };
      }
      const combinedCodes = [...existingCodes, newCode];
      const result = await applyDiscounts(combinedCodes);
      if (result && result.networkError) {
        showError(MSG_NETWORK);
        return { ok: false, reason: 'network' };
      }
      if (result.invalidCodes.map(c => c.toUpperCase()).includes(newCode)) {
        showError(MSG_INVALID);
        return { ok: false, reason: 'invalid' };
      }
      return { ok: true, reason: 'applied' };
    }

    // BUSY GUARD (owner brief 2026-08-27: "adding and removing and the speed and
    // all"). Same class of bug as the Remove path below -- a second tap on Apply
    // during the in-flight round trip fired another overlapping request. Shares
    // the SAME window.hrDiscountActionBusy flag as Remove, so a shopper cannot
    // fire an Apply and a Remove at once either (they would race the same cart
    // session). Also disables every Remove pill while Apply is in flight, and
    // vice versa (see the Remove handler), so only one discount action is ever
    // outstanding at a time.
    async function handleApplyClick() {
      if (window.hrDiscountActionBusy) return;
      // Resolve the button LIVE: the closure's node may already be detached
      // (see the delegated-binding note below), and disabling a detached node
      // would leave the on-screen button looking idle while work is in flight.
      const applyBtn = document.getElementById('apply-discount-btn');
      if (!applyBtn) return;
      window.hrDiscountActionBusy = true;
      applyBtn.disabled = true;
      applyBtn.setAttribute('aria-busy', 'true');
      applyBtn.classList.add('is-applying');
      document.querySelectorAll('.remove-discount').forEach((btn) => { btn.disabled = true; });
      try {
        await submitEnteredCode();
      } catch (err) {
        // Defensive: submitEnteredCode/applyDiscounts should never throw (the
        // network path above returns a result instead), but if something
        // unexpected does, fail visibly rather than as a silent console error.
        showError(MSG_NETWORK);
      } finally {
        window.hrDiscountActionBusy = false;
        applyBtn.disabled = false;
        applyBtn.removeAttribute('aria-busy');
        applyBtn.classList.remove('is-applying');
        document.querySelectorAll('.remove-discount').forEach((btn) => { btn.disabled = false; });
      }
    }

    /* DELEGATED BINDING (regression fix, 2026-08-27).
       assets/cart-drawer.js now swaps the children of #discount-code-container
       on every cart render (added there so a removed pill actually repaints).
       That swap DESTROYS this button node, and a listener bound straight to
       the node dies with it -- measured on an iPhone 13 profile: after one
       add-to-cart, tapping Apply fired no /cart/update request at all, showed
       no error, and did nothing. initDiscountScript() does re-run on
       PUB_SUB_EVENTS.cartUpdate, but that publish does not reliably land after
       the swap, so re-binding alone is not enough.
       The listener now lives on `document`, which is never replaced, and
       dispatches to whichever handler the latest init installed. */
    window.__hrApplyHandler = handleApplyClick;

    window.__hrRemoveHandler = async (event) => {
      const removeBtnEl = event.target.closest('.remove-discount');
      if (!removeBtnEl) return;

      // BUSY GUARD (owner-reported live bug, 2026-08-26: real customer tapped
      // Remove five times in ten seconds and gave up, removing the PRODUCT
      // instead). Root cause was that every tap during the in-flight
      // /cart/update.js round trip fired ANOTHER overlapping remove request;
      // on a slow mobile connection whichever response landed LAST could win
      // and leave the code re-applied even after the shopper removed it.
      // window-scoped, not module-scoped, because this file is loaded twice
      // on the cart page (main cart items + drawer) -- same reason the other
      // guards in this file live on window.
      if (window.hrDiscountActionBusy) return;
      window.hrDiscountActionBusy = true;

      clearMessages();
      const pill = removeBtnEl.closest('.discount-pill');
      if (!pill) { window.hrDiscountActionBusy = false; return; }
      const codeToRemove = pill.getAttribute('data-code');
      if (!codeToRemove) { window.hrDiscountActionBusy = false; return; }

      // Visible pending state: the reason people tapped five times is that
      // nothing on screen told them the first tap had registered. Native
      // `disabled` also stops a second click firing at the browser level as a
      // second line of defence (belt-and-suspenders with the flag above), and
      // the CSS pointer-events:none covers the old iOS Safari quirk where a
      // disabled button can still take a touch in some versions.
      removeBtnEl.disabled = true;
      removeBtnEl.setAttribute('aria-busy', 'true');
      pill.classList.add('is-removing');
      if (applyBtn) applyBtn.disabled = true;
      // Every OTHER pill's remove button is disabled too, mirroring the Apply
      // guard above: only one discount action outstanding at a time, whichever
      // control started it.
      document.querySelectorAll('.remove-discount').forEach((btn) => {
        if (btn !== removeBtnEl) btn.disabled = true;
      });

      // Removing a code by hand also retires the "your code is applied" note, so
      // the cart never claims a discount that is no longer on the order.
      if (window.hrReturnedCodeNote && window.hrReturnedCodeNote.code === codeToRemove.toUpperCase()) {
        window.hrReturnedCodeNote = null;
        document.querySelectorAll('[data-hr-code-returned]').forEach((el) => { el.hidden = true; });
      }
      try {
        const existingCodes = getCurrentDiscounts();
        const newDiscountList = existingCodes.filter(c => c !== codeToRemove.toUpperCase());
        const result = await applyDiscounts(newDiscountList);
        if (result && result.networkError) {
          showError(MSG_NETWORK);
        }
      } catch (err) {
        // Defensive: applyDiscounts should never throw (its own /cart.js fetch
        // is caught and returned as networkError above), but if something
        // unexpected still does, fail visibly rather than as a silent,
        // unhandled console error.
        showError(MSG_NETWORK);
      } finally {
        window.hrDiscountActionBusy = false;
        if (applyBtn) applyBtn.disabled = false;
        // If the pill is still on screen (e.g. the section refresh is slow),
        // restore it to a normal, re-tappable state rather than leaving it
        // stuck disabled forever.
        removeBtnEl.disabled = false;
        removeBtnEl.removeAttribute('aria-busy');
        pill.classList.remove('is-removing');
        document.querySelectorAll('.remove-discount').forEach((btn) => { btn.disabled = false; });
      }
    };

    /* Installed once per page, never re-registered, so repeated
       initDiscountScript() calls cannot stack duplicate listeners. */
    if (!window.__hrDiscountDelegated) {
      window.__hrDiscountDelegated = true;
      /* Enter / the mobile keyboard's Go key submits the typed code (owner
         2026-08-28: affiliate-code shoppers type and hit Enter; nothing
         happened because this input has no surrounding <form>). Document-level
         for the same reason as the click handler below: the input node is
         replaced on every cart render. The busy guard inside the apply handler
         makes a held-down or repeated Enter harmless. */
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        const t = event.target;
        if (!t || typeof t.closest !== 'function' || !t.closest('#discount-input')) return;
        event.preventDefault();
        if (typeof window.__hrApplyHandler === 'function') window.__hrApplyHandler();
      });
      document.addEventListener('click', function (event) {
        const t = event.target;
        if (!t || typeof t.closest !== 'function') return;
        /* Offer-banner code chip (owner brief 2026-08-28): tapping HARVEST10
           copies it AND pre-fills the field, so applying is one more tap.
           Resolved live, same reason as the apply/remove handlers: the chip
           node is replaced on every cart render. Clipboard failure (http,
           permissions) is fine -- the pre-filled input alone completes the
           flow, so the catch only skips the "copied" affordance. */
        const chip = t.closest('#hr-offer-code');
        if (chip) {
          /* ONE TAP (owner correction 2026-08-28: "most of the people who are
             ordering are not tech smart or fast... stuff needs to work in one
             tap"). The first build copied the code and pre-filled the field,
             which still left a second tap on Apply that he himself missed.
             Now the tap IS the apply: fill the live input, then run the exact
             same handler the Apply button runs -- same busy guard, same error
             surface, same repaint. The banner hides on the next render because
             the cart then carries a code. Clipboard is gone on purpose: it was
             a programmer's affordance, not a shopper's. */
          const code = chip.getAttribute('data-code') || '';
          const inp = document.getElementById('discount-input');
          if (inp) { inp.value = code; inp.dispatchEvent(new Event('input', { bubbles: true })); }
          const label = chip.querySelector('.hr-discount-offer__codetext');
          if (label) label.textContent = 'Adding…';
          chip.classList.add('is-copied');
          if (typeof window.__hrApplyHandler === 'function') window.__hrApplyHandler();
          return;
        }
        if (t.closest('#apply-discount-btn')) {
          if (typeof window.__hrApplyHandler === 'function') window.__hrApplyHandler();
          return;
        }
        const rb = t.closest('.remove-discount');
        if (rb && rb.closest('#applied-discounts')) {
          if (typeof window.__hrRemoveHandler === 'function') window.__hrRemoveHandler(event);
        }
      });
    }

    // ---- The quiz code coming home ----
    renderReturnedNote();

    if (window.hrReturnedCodeHandled) return;
    const returnedCode = readReturnedCode();
    if (!returnedCode) return;
    window.hrReturnedCodeHandled = true;

    // Keep it in sessionStorage first, then take it out of the URL, so a reload
    // in the middle of this never drops the code on the floor.
    ssSet(PENDING_CODE_KEY, returnedCode);
    cleanReturnParamsFromUrl();

    // Every copy of the field on the page gets the code, because the cart page
    // renders the drawer and the page footer form at the same time.
    document.querySelectorAll('#discount-input').forEach((input) => {
      input.value = returnedCode;
      input.classList.add('is-hr-prefilled');
    });
    setTimeout(() => {
      document.querySelectorAll('#discount-input').forEach((i) => i.classList.remove('is-hr-prefilled'));
    }, 2600);

    const alreadyOnCart = getCurrentDiscounts().includes(returnedCode);
    window.hrReturnedCodeNote = { code: returnedCode, applied: alreadyOnCart };
    renderReturnedNote();
    ssRemove(PENDING_CODE_KEY);
    ssRemove(ORIGIN_KEY);
    ssRemove(RETURN_URL_KEY);

    if (alreadyOnCart) return;
    submitEnteredCode().then((result) => {
      window.hrReturnedCodeNote = { code: returnedCode, applied: !!(result && result.ok) };
      renderReturnedNote();
    }).catch(() => {
      window.hrReturnedCodeNote = { code: returnedCode, applied: false };
      renderReturnedNote();
    });
  }

  function bootReturnFlow() {
    if (wantsCartOpen() && !window.hrReturnedCodeHandled) openCartDrawer();
    initDiscountScript();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootReturnFlow);
  } else {
    bootReturnFlow();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      initDiscountScript();
    });
  }
})();
